import type { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import { NotFoundView, RouteErrorView } from '@/features/shell';
import { Skeleton } from '@/design-system';

import { routeTree } from '../routeTree.gen';

/**
 * Search params are flat text (lists are comma-separated, numbers are coerced by each
 * route's Zod schema), so plain URLSearchParams keeps URLs readable: ?new=1, ?q=4471.
 */
export function parseSearch(search: string): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(search.startsWith('?') ? search.slice(1) : search));
}
export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, typeof value === 'string' ? value : JSON.stringify(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export interface RouterContext {
  queryClient: QueryClient;
}

function DefaultPending() {
  return (
    <div className="flex flex-col gap-3 px-(--content-padding) py-5" aria-busy="true">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-80" />
      <div className="mt-4 flex flex-col gap-2">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    </div>
  );
}

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    parseSearch,
    stringifySearch,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultPendingMs: 300,
    defaultPendingMinMs: 200,
    defaultPendingComponent: DefaultPending,
    defaultErrorComponent: ({ error, reset }) => <RouteErrorView error={error} reset={reset} />,
    defaultNotFoundComponent: () => <NotFoundView />,
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
  interface StaticDataRouteOption {
    /** i18n key (shell namespace) for the breadcrumb. */
    crumb?: string;
    /** For record routes: where the section crumb links (the record crumb comes from loader data). */
    crumbTo?: string;
    /** Section crumbs shown before this route's own (e.g. Setup › Imports above an import outside the Setup tabs). */
    crumbParents?: readonly { crumb: string; to: string }[];
  }
}
